import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { ApplicationUser } from '@models/application-user';
import { School } from '@models/school';
import { IUser } from '@models/user';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@shared/button/button.component';
import { RoleType } from '@shared/enums/role-type';
import {
  generalUserFields,
  primaryFormControlNames
} from '@shared/manage-users/manage-users-form/constants/form-field-setup-data';
import { UserFormMode } from '@shared/manage-users/manage-users-form/constants/form-mode';
import { EditUserFormHelpers } from '@shared/manage-users/manage-users-form/helpers/edit-user-mode-form-mappers';
import {
  ManageUsersFormComponent
} from '@shared/manage-users/manage-users-form/manage-users-form.component';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import { UnsavedChangesModalComponent } from '@shared/modals/unsaved-changes/unsaved-changes-modal.component';
import { copyObject } from '@shared/zb-object-helper/object-helper';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, finalize, Observable, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SchoolsFacade } from 'src/app/schools/store/schools.facade';
import { EditUserModalParams } from './branded-edit-user-params';

@Component({
  selector: 'zbp-branded-edit-user',
  templateUrl: './branded-edit-user.component.html',
  styleUrls: ['./branded-edit-user.component.scss'],
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    CommonModule,
    ManageUsersFormComponent,
    ModalTemplateComponent,
  ]
})
export class BrandedEditUserComponent implements OnInit, OnDestroy {

  static readonly DEFAULT_MODAL_DIALOG_CLASS = '';

  @Input() currentSchool: School = null;

  /**
   * Current application user that is authenticated.
   *
   * This is not the user being edited.
   */
  currentAuthedUser: IUser;
  /**
   * List of schools that are available to the current Application Usernpm run start
   *
   */
  roleToEdit: RoleType = null;
  currentAuthedUserSchools: School[] = [];
  showParentFieldsSearch: boolean = false;
  isLoading: boolean = true;
  isSaving: boolean = false;
  /**  Used to display the form has unsaved changes message. */
  hasUnsavedChanges = new BehaviorSubject<boolean>(false);
  bypassUnsavedChanges: boolean = false;
  editFormMode: UserFormMode = UserFormMode.Edit;
  formGroupContainer: FormGroup;
  form: FormGroup = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    username: new FormControl('', Validators.required),
  });

  private subscriptions: Subscription[] = [];
  private _userToEditOriginal: IUser;
  private _originalSchoolId: string;

  readonly editUserGroupName: string =  'editUser';
  readonly studentRoleType = RoleType.Student;

  constructor(
    private activeModal: NgbActiveModal,
    private userService: UserService,
    private toastr: ToastrService,
    private schoolsFacade: SchoolsFacade) {
    this.updateSelectedUser = this.updateSelectedUser.bind(this);
  }

  public static open(ngbModal: NgbModal, params: EditUserModalParams): NgbModalRef {
    const openConfirmModal = async () => {
      const confirmCloseModalRef = UnsavedChangesModalComponent.open(ngbModal);

      return confirmCloseModalRef.result;
    };

    const modalRef = ngbModal.open(
      BrandedEditUserComponent,
      {
        ...params?.options,
        beforeDismiss: () => (
          !modalRef.componentInstance?.bypassUnsavedChanges && modalRef.componentInstance?.hasUnsavedChanges?.value
            ? openConfirmModal()
            : true),
        // only use defaults if params does not provide value
        modalDialogClass: params?.options?.modalDialogClass
          ?? BrandedEditUserComponent.DEFAULT_MODAL_DIALOG_CLASS
      },
    );

    const component = modalRef.componentInstance as BrandedEditUserComponent;
    component.currentSchool = params.currentSchool;
    component.userToEditOriginal = params.userToEditOriginal;
    component.roleToEdit = params.roleToEdit;
    component.showParentFieldsSearch = params.showParentFieldsSearch;
    return modalRef;
  }

  ngOnInit(): void {
    this.formGroupContainer = new FormGroup({});
    this.currentAuthedUser = this.userService.user;
    // Managing users district-wide doesn't provide a School
    this._originalSchoolId = this.currentSchool?.schoolId;

    // Subscribe to the schools data here
    this.subscriptions.push(
      this.schoolsFacade.schools$.subscribe((schools: School[]) => {
        this.currentAuthedUserSchools = schools || [];
      })
    );
    this.isLoading = false;
  }

  updateSelectedUser(newUser: IUser, role: RoleType) {
    this.roleToEdit = role;
    this.userToEditOriginal = newUser;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Set the original state of user that's being edited.
   * This should not be updated once its set as it is used to compare changes that are made in the form.
   */
  set userToEditOriginal(value: IUser) {
    this._userToEditOriginal = copyObject(value, ApplicationUser);
  }

  /**
   * Gets the original state of user that's being edited.
   * Can be used to compare new values in the form vs the original values of the user.
   */
  get userToEditOriginal(): IUser {
    return this._userToEditOriginal;
  }

  dismiss(): void {
    this.activeModal.dismiss(null);
  }

  save(): void {
    this.isSaving = true;
    this.subscriptions.push(
      this.updateUser()
        .pipe(
          finalize(() => {
            this.isSaving = false;
          }),
          catchError((err: HttpErrorResponse) => of(new ApiResponse<IUser[]>(false, err.error))),
        )
        .subscribe((res: ApiResponse<IUser>) => {
          if (res.success) {
            const updatedUser = res.response;

            if (this.roleToEdit === RoleType.Student) {
              updatedUser['refreshTable'] = true;
            }

            if (!updatedUser) {
              console.error(updatedUser);
            }
            this.activeModal.close(updatedUser);
            this.toastr.success(`${updatedUser.getDisplayName} modified successfully.`);
          } else if (res.messages && res.messages?.length > 0) {
            res.messages.forEach((message) => {
              console.error('TOAST MESSAGE: ', message);
              return this.toastr.error(message);
            });
          } else {
            this.toastr.error(`An error occurred saving ${this.userToEditOriginal.getDisplayName}.`);
          }
        })
    );
  }

  updateUser(): Observable<ApiResponse<IUser>> {
    const usersFormArray = this.formGroupContainer
      .get(this.editUserGroupName)
      .get(primaryFormControlNames.newUsers) as FormArray;

    // editing a single user will have it be the only array entry
    const userBeingEdited = usersFormArray
      .controls
      .find((control: FormControl) => control
        .get(generalUserFields.userId.name).value === this.userToEditOriginal.userId);

    const selectedSchoolId = userBeingEdited.get(generalUserFields.schoolId.name)?.value;

    let result$: Observable<ApiResponse<IUser>>;

    // Determine whether the student's selected school differs from original.
    const isOriginalSchoolAndSelectedDifferent = !!(
      this._originalSchoolId && selectedSchoolId && this._originalSchoolId !== selectedSchoolId
    );

    // originalSchoolId is null but user's schoolId changed in case of district-wide selected
    const isNoOriginalButUserSchoolChanged = !!(
      !this._originalSchoolId && selectedSchoolId && this.userToEditOriginal?.schools?.[0]
      && this.userToEditOriginal.schools[0].schoolId !== selectedSchoolId
    );

    if (isOriginalSchoolAndSelectedDifferent || isNoOriginalButUserSchoolChanged) {
      /* Transfer Student */
      result$ = this.transferStudent(selectedSchoolId);
    } else {
      const allUsers = EditUserFormHelpers
        .mapEditUserRequestData(this.userToEditOriginal, usersFormArray, selectedSchoolId, this.roleToEdit);
      result$ = this.userService.upsertUsers(allUsers, this._originalSchoolId)
        .pipe(
          map((res: ApiResponse<IUser[]>) => {
            const isResponse = res.response && res.response.length;
            let updatedUser: IUser = null;
            if (this.roleToEdit !== RoleType.Student) {
              updatedUser = isResponse ? res.response[0] : null;
            } else {
              // find the student, additional elements will be parents
              updatedUser = isResponse ? res.response.find(u => u.studentUserName !== null) : null;
              if (updatedUser && res.response.length > 1) {
                // new parents added will not be in the user's associations
                for (let i = 0; i < res.response.length; i++) {
                  const user = res.response[i];
                  if (user.userId !== updatedUser.userId
                    && updatedUser.userAssociations.findIndex(u => u.userId === user.userId) === -1) {
                    updatedUser.userAssociations.push(user);
                  }
                }
              }
            }
            return new ApiResponse<IUser>(true, {
              response: updatedUser,
              messages: res.messages,
            });
          }));
    }
    return result$;
  }

  transferStudent(newSchoolId: string): Observable<ApiResponse<IUser>> {
    const allSchools = this.currentAuthedUserSchools;
    const usersFormArray = this.formGroupContainer
      .get(this.editUserGroupName)
      .get(primaryFormControlNames.newUsers) as FormArray;

    const usersToUpdateArray = EditUserFormHelpers.mapStudentTransferRequestData(
      this.userToEditOriginal,
      usersFormArray,
      newSchoolId,
      allSchools,
      this._originalSchoolId
    );

    return this.userService.upsertUsers(usersToUpdateArray)
      .pipe(
        map((res: ApiResponse<IUser[]>) => new ApiResponse<IUser>(
          true,
          {
            ...res,
            response: res.response[0],
          }))
      );
  }

  handleBypassUnsavedChanges(value: boolean) {
    this.bypassUnsavedChanges = value;
  }
}
